import { Box, ButtonBase } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

export interface SegmentedNavItem {
  id: string;
  label: string;
  to: string;
}

/**
 * The approved segmented switch: a quiet track holding a few page links, the
 * current one raised and bold. Links (not tabs) because each segment is its
 * own route; the current one carries aria-current="page".
 */
export function SegmentedNav({
  label,
  items,
  active,
  wrap = false,
}: {
  label: string;
  items: readonly SegmentedNavItem[];
  active: string;
  /** Two per row on phones when the labels can't share one row. */
  wrap?: boolean;
}) {
  return (
    <Box
      component="nav"
      aria-label={label}
      sx={{
        display: "flex",
        gap: 0.5,
        p: 0.5,
        borderRadius: 2.5,
        bgcolor: "action.hover",
        overflowX: "auto",
        scrollbarWidth: "none",
        ...(wrap ? { flexWrap: { xs: "wrap", sm: "nowrap" } } : {}),
      }}
    >
      {items.map((item) => {
        const current = item.id === active;
        return (
          <ButtonBase
            key={item.id}
            component={RouterLink}
            to={item.to}
            aria-current={current ? "page" : undefined}
            sx={{
              flex: { xs: wrap ? "1 1 calc(50% - 4px)" : "1 1 0", sm: "0 0 auto" },
              minHeight: 36,
              px: { xs: 1, sm: 2 },
              borderRadius: 2,
              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
              fontWeight: current ? 700 : 500,
              whiteSpace: "nowrap",
              color: current ? "text.primary" : "text.secondary",
              bgcolor: current ? "background.default" : "transparent",
              "&:hover": { color: "text.primary" },
              "&.Mui-focusVisible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
            }}
          >
            {item.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
